import { isHistoryIntent } from "./assistantRules";

const ARABIC_TEXT_PATTERN = /[\u0600-\u06ff]/;
const NUTRIENT_KEYS = ["potassium_mg", "phosphorus_mg", "sodium_mg", "protein_g"];

const FOOD_LOG_INTENT =
  /\b(i\s+ate|i\s+had|log\s+.+today|log\s+.+now|log\s+it)\b|أكلت|اكلت|سجل\s+.+اليوم|سجّل\s+.+اليوم|سجلها|سجلت/iu;
const ALREADY_LOGGED_INTENT = /\b(already\s+logged|i\s+logged|logged\s+it)\b|لقد\s+سجلت|سجلت/iu;

const STOP_WORDS = new Set([
  "i",
  "ate",
  "had",
  "an",
  "a",
  "one",
  "medium",
  "small",
  "large",
  "serving",
  "servings",
  "log",
  "today",
  "now",
  "already",
  "logged",
  "it",
  "the",
  "in",
  "app",
  "application",
  "متوسطة",
  "متوسط",
  "صغيرة",
  "صغير",
  "كبيرة",
  "كبير",
  "حبة",
  "واحدة",
  "اكلت",
  "أكلت",
  "سجل",
  "سجّل",
  "سجلها",
  "سجلت",
  "اليوم",
  "لقد",
  "في",
  "بالتطبيق",
  "التطبيق",
]);

const SERVING_HINTS = [
  { terms: ["medium", "متوسطة", "متوسط"], label: { en: "medium", ar: "متوسطة" } },
  { terms: ["small", "صغيرة", "صغير"], label: { en: "small", ar: "صغيرة" } },
  { terms: ["large", "كبيرة", "كبير"], label: { en: "large", ar: "كبيرة" } },
];

const ALIASES = [
  ["grilled chicken", "grilled chicken"],
  ["white rice", "white rice"],
  ["brown rice", "brown rice"],
  ["rice", "rice"],
  ["رز", "رز"],
  ["أرز", "أرز"],
  ["ارز", "ارز"],
  ["chicken", "chicken"],
  ["دجاج", "دجاج"],
  ["apple", "apple"],
  ["تفاحة", "تفاحة"],
  ["تفاح", "تفاح"],
  ["pineapple", "pineapple"],
  ["أناناس", "أناناس"],
  ["اناناس", "اناناس"],
  ["banana", "banana"],
  ["موزة", "موزة"],
  ["موز", "موز"],
  ["cucumber", "cucumber"],
  ["خيار", "خيار"],
  ["parsley", "parsley"],
  ["بقدونس", "بقدونس"],
];

const safeText = (value, maxLength = 120) =>
  typeof value === "string" ? value.trim().slice(0, maxLength) : "";

export const getActionLanguage = (text) => (ARABIC_TEXT_PATTERN.test(String(text || "")) ? "ar" : "en");

export const normalizeFoodActionText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u064b-\u065f\u0670]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const nutrientPayload = (food, quantity = 1) =>
  NUTRIENT_KEYS.reduce((acc, key) => {
    acc[key] = numberValue(food?.[key]) * quantity;
    return acc;
  }, {});

const foodDisplayName = (food, language = "en") => {
  if (language === "ar") return safeText(food?.name_ar || food?.name || food?.name_en || "الطعام");
  return safeText(food?.name || food?.name_en || food?.name_ar || "Food");
};

const foodNames = (food) =>
  [food?.name, food?.name_en, food?.name_ar].map(normalizeFoodActionText).filter(Boolean);

const foodSearchText = (food) =>
  normalizeFoodActionText([food?.name, food?.name_en, food?.name_ar, food?.category, food?.serving_size].filter(Boolean).join(" "));

const hasWholeWord = (text, word) => {
  const normalizedText = normalizeFoodActionText(text);
  const normalizedWord = normalizeFoodActionText(word);
  if (!normalizedText || !normalizedWord) return false;
  const escapedWord = normalizedWord.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|\\s)${escapedWord}(\\s|$)`, "u").test(normalizedText);
};

const foodMatchesExactOrWholeWord = (food, names) => {
  const normalizedNames = names.map(normalizeFoodActionText).filter(Boolean);
  const exactNames = foodNames(food);
  if (exactNames.some((name) => normalizedNames.includes(name))) return true;
  return normalizedNames.some((name) =>
    [food?.name, food?.name_en, food?.name_ar].filter(Boolean).some((value) => hasWholeWord(value, name))
  );
};

const uniqueFoods = (foods) => {
  const seen = new Set();
  return foods.filter((food) => {
    const key = String(food?.id ?? foodDisplayName(food));
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const choiceAliasesForFood = (food) => {
  const normalizedName = normalizeFoodActionText(food?.name || food?.name_en || food?.name_ar || "");
  const aliases = [food?.name, food?.name_en, food?.name_ar].filter(Boolean);

  if (normalizedName.includes("white rice")) {
    aliases.push("white", "white rice", "رز أبيض", "ارز ابيض", "الأرز الأبيض", "الارز الابيض");
  }

  if (normalizedName.includes("brown rice")) {
    aliases.push("brown", "brown rice", "رز بني", "رز أسمر", "ارز بني", "ارز اسمر", "الأرز البني", "الارز البني");
  }

  return [...new Set(aliases.map(normalizeFoodActionText).filter(Boolean))];
};

const candidatesForAlias = (foods, alias) => {
  const normalizedAlias = normalizeFoodActionText(alias);

  if (["rice", "رز", "أرز", "ارز"].includes(normalizedAlias)) {
    return uniqueFoods(foods.filter((food) => {
      const text = foodSearchText(food);
      return text.includes("white rice") || text.includes("brown rice") || text.includes("رز ابيض") || text.includes("رز بني");
    }));
  }

  if (["chicken", "grilled chicken", "دجاج"].includes(normalizedAlias)) {
    return uniqueFoods(foods.filter((food) => {
      const text = foodSearchText(food);
      return text.includes("chicken breast") || text.includes("chicken") || text.includes("دجاج");
    }));
  }

  if (["apple", "تفاحة", "تفاح"].includes(normalizedAlias)) {
    return uniqueFoods(foods.filter((food) =>
      foodMatchesExactOrWholeWord(food, ["apple", "تفاحة", "تفاح"])
    ));
  }

  if (["pineapple", "أناناس", "اناناس"].includes(normalizedAlias)) {
    return uniqueFoods(foods.filter((food) =>
      foodMatchesExactOrWholeWord(food, ["pineapple", "أناناس", "اناناس"])
    ));
  }

  if (["banana", "موزة", "موز"].includes(normalizedAlias)) {
    return uniqueFoods(foods.filter((food) =>
      foodMatchesExactOrWholeWord(food, ["banana", "موزة", "موز"])
    ));
  }

  if (["cucumber", "خيار"].includes(normalizedAlias)) {
    return uniqueFoods(foods.filter((food) => {
      const text = foodSearchText(food);
      return text.includes("cucumber") || text.includes("خيار");
    }));
  }

  return uniqueFoods(foods.filter((food) => {
    const names = foodNames(food);
    const searchText = foodSearchText(food);
    return names.some((name) => name === normalizedAlias) || searchText.includes(normalizedAlias);
  }));
};

const getServingHint = (question, language) => {
  const normalizedQuestion = normalizeFoodActionText(question);
  const hint = SERVING_HINTS.find(({ terms }) =>
    terms.some((term) => normalizedQuestion.split(/\s+/).includes(normalizeFoodActionText(term)))
  );
  return hint ? hint.label[language] : "";
};

const detectTerms = (question, foods = []) => {
  const normalizedQuestion = normalizeFoodActionText(question);
  const terms = new Map();
  const addTerm = (term, display = term) => {
    const normalizedTerm = normalizeFoodActionText(term);
    if (!normalizedTerm) return;

    for (const existingTerm of terms.keys()) {
      if (existingTerm === normalizedTerm) return;
      if (existingTerm.includes(normalizedTerm)) return;
      if (normalizedTerm.includes(existingTerm)) terms.delete(existingTerm);
    }

    terms.set(normalizedTerm, { term: normalizedTerm, display });
  };

  ALIASES.forEach(([alias, display]) => {
    if (normalizedQuestion.includes(normalizeFoodActionText(alias))) addTerm(alias, display);
  });

  foods.forEach((food) => {
    foodNames(food).forEach((name) => {
      if (name && normalizedQuestion.includes(name)) addTerm(name, foodDisplayName(food, getActionLanguage(question)));
    });
  });

  if (terms.size) return [...terms.values()];

  const fallback = normalizedQuestion
    .split(/\s+/)
    .filter((word) => word.length > 1 && !STOP_WORDS.has(word))
    .join(" ");

  return fallback ? [{ term: fallback, display: fallback }] : [];
};

const isSimpleFoodOnlyRequest = (text, terms) => {
  if (terms.length !== 1) return false;
  const termWords = new Set(terms[0].term.split(/\s+/));
  return normalizeFoodActionText(text)
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => STOP_WORDS.has(word) || termWords.has(word));
};

const formatOptions = (foods, language) => {
  const names = foods.map((food) => foodDisplayName(food, language)).filter(Boolean);
  return names.join(language === "ar" ? " أو " : " or ");
};

export const resolveFoodClarificationChoice = (reply, clarification) => {
  const normalizedReply = normalizeFoodActionText(reply);
  if (!normalizedReply || !clarification?.choices?.length) return null;

  return clarification.choices.find((choice) => {
    const aliases = choice.aliases?.length ? choice.aliases : choiceAliasesForFood(choice.food);
    return aliases.includes(normalizedReply);
  }) || null;
};

const itemExistsToday = (food, todayHistory) => {
  const names = new Set(foodNames(food));
  return Boolean((todayHistory?.items || []).some((item) => {
    const itemName = normalizeFoodActionText(item.name);
    if (names.has(itemName)) return true;
    return item.ingredients?.some((ingredient) => names.has(normalizeFoodActionText(ingredient.name)));
  }));
};

export const createLogPayloadFromFood = (food, quantity = 1, loggedAt = new Date().toISOString()) => ({
  food_id: food?.id ?? null,
  food_name: safeText(food?.name || food?.name_en || food?.name_ar || ""),
  category: safeText(food?.category || ""),
  serving_size: safeText(food?.serving_size || ""),
  quantity,
  ...nutrientPayload(food, quantity),
  logged_at: loggedAt,
});

export const buildPendingFoodLogAction = ({ food, question = "", language = getActionLanguage(question), todayHistory } = {}) => {
  if (!food) return null;

  if (ALREADY_LOGGED_INTENT.test(String(question || "")) && itemExistsToday(food, todayHistory)) {
    return {
      kind: "message",
      answer: language === "ar"
        ? "يظهر في السجل أن هذا العنصر مسجل اليوم."
        : "Your history shows this item is logged today.",
    };
  }

  const quantity = 1;
  const displayName = foodDisplayName(food, language);
  const servingHint = getServingHint(question, language);
  const action = {
    id: `ai-log-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    food,
    foodName: displayName,
    quantity,
    servingHint,
    servingSize: safeText(food?.serving_size || ""),
    language,
    payload: createLogPayloadFromFood(food, quantity),
  };

  return {
    kind: "pending",
    action,
    answer: ALREADY_LOGGED_INTENT.test(String(question || ""))
      ? language === "ar"
        ? "لا يظهر هذا العنصر في سجل اليوم بعد. يمكنك التأكيد أدناه لتسجيله."
        : "I do not see it in today's history yet. You can confirm below to log it."
      : language === "ar"
        ? `وجدت ${displayName}. هل تريد تأكيد تسجيله في سجل اليوم؟`
        : `I found ${displayName}. Do you want to log it to today's history?`,
  };
};

export const buildAssistantFoodLogAction = ({ question, foods = [], todayHistory } = {}) => {
  const text = String(question || "").trim();
  if (!text || isHistoryIntent(text)) return null;

  const language = getActionLanguage(text);
  const safeFoods = Array.isArray(foods) ? foods : [];
  const terms = detectTerms(text, safeFoods);
  if (!terms.length) return null;

  const hasLogIntent = FOOD_LOG_INTENT.test(text) || ALREADY_LOGGED_INTENT.test(text);
  if (!hasLogIntent && !isSimpleFoodOnlyRequest(text, terms)) return null;

  const matches = terms.map((term) => ({
    term,
    candidates: candidatesForAlias(safeFoods, term.term),
  }));

  const ambiguous = matches.find((match) => match.candidates.length > 1);
  if (ambiguous) {
    return {
      kind: "clarification",
      answer: language === "ar"
        ? `أي نوع تريد استخدامه: ${formatOptions(ambiguous.candidates, language)}؟`
        : `Which one should I use: ${formatOptions(ambiguous.candidates, language)}?`,
      clarification: {
        id: `ai-clarify-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        originalQuestion: text,
        language,
        choices: ambiguous.candidates.map((food) => ({
          food,
          label: foodDisplayName(food, language),
          aliases: choiceAliasesForFood(food),
        })),
      },
    };
  }

  const missing = matches.find((match) => match.candidates.length === 0);
  if (missing) {
    const missingName = safeText(missing.term.display || missing.term.term, 80);
    return {
      kind: "message",
      answer: language === "ar"
        ? `لم أجد ${missingName} في قاعدة الطعام الحالية. يرجى اختيار أو إضافة طعام موثوق أولاً.`
        : `I could not find ${missingName} in the current food database. Please choose or add a verified food first.`,
    };
  }

  const matchedFoods = matches.map((match) => match.candidates[0]).filter(Boolean);
  if (matchedFoods.length !== 1) {
    return {
      kind: "message",
      answer: language === "ar"
        ? "يرجى تسجيل طعام واحد في كل مرة أو استخدام حاسبة الوجبة للمكونات المتعددة."
        : "Please log one food at a time, or use Meal Mixer for multiple ingredients.",
    };
  }

  return buildPendingFoodLogAction({
    food: matchedFoods[0],
    question: text,
    language,
    todayHistory,
  });
};
